/**
 * UpgradePrompt — конверсионное модальное окно с предложением апгрейда
 *
 * Usage:
 *   <UpgradePrompt featureId="monthly" requiredTier="premium" onClose={() => setShow(false)} />
 *
 * Особенности:
 * - Крупное окно (700px) с анимацией появления
 * - Эмоциональный дизайн с градиентами и свечением
 * - Социальное доказательство (число пользователей)
 * - Сравнение тарифов в таблице
 * - Конкретные преимущества с иконками
 * - CTA с пульсацией и акцентом на доступность
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SubscriptionTier,
  TIER_ORDER,
  TIER_LABELS,
  TIER_PRICES,
  TIER_BADGE_COLORS,
} from '../../../shared/types/access';

interface UpgradePromptProps {
  featureId: string;
  featureName?: string;
  userTier?: SubscriptionTier;
  requiredTier: SubscriptionTier;
  onClose?: () => void;
  showInModal?: boolean;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  featureId,
  featureName,
  userTier = 'free',
  requiredTier,
  onClose,
  showInModal = true,
}) => {
  const navigate = useNavigate();
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const userLevel = TIER_ORDER[userTier] ?? 0;
  const requiredLevel = TIER_ORDER[requiredTier] ?? 0;
  const hasAccess = userLevel >= requiredLevel;

  if (hasAccess) return null;

  const currentUserPrice = TIER_PRICES[userTier]?.monthly || 0;
  const requiredPrice = TIER_PRICES[requiredTier]?.monthly || 0;
  const priceDiff = requiredPrice - currentUserPrice;
  const perDay = Math.round(requiredPrice / 30);

  const requiredColor = TIER_BADGE_COLORS[requiredTier] || '#d4af37';

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => onClose?.(), 300);
  };

  const handleUpgrade = () => {
    navigate(`/subscription?upgrade=${requiredTier}&from=${featureId}`);
    onClose?.();
  };

  const benefits = getTierBenefits(requiredTier);
  const tiers = getTiersForComparison(requiredTier);

  const rub = '\u20BD';
  const mdash = '\u2014';
  const coffee = '\u2615';

  const ModalContent = (
    <div
      className="w-full mx-auto rounded-3xl overflow-hidden"
      style={{
        maxWidth: '700px',
        background: 'linear-gradient(145deg, rgba(10, 8, 20, 0.98) 0%, rgba(30, 20, 60, 0.95) 50%, rgba(10, 25, 47, 0.95) 100%)',
        backdropFilter: 'blur(30px)',
        border: `2px solid ${requiredColor}50`,
        boxShadow: `0 0 80px ${requiredColor}20, 0 25px 80px rgba(0, 0, 0, 0.6)`,
        transform: isVisible && !isClosing ? 'scale(1)' : 'scale(0.9)',
        opacity: isVisible && !isClosing ? 1 : 0,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Animated top glow */}
      <div
        className="h-1 w-full"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${requiredColor} 30%, ${requiredColor} 70%, transparent 100%)`,
          animation: 'pulse-glow 2s ease-in-out infinite',
        }}
      />

      {/* Header */}
      <div className="px-8 py-6 text-center relative">
        {onClose && (
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full transition-all hover:scale-110"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#888',
              fontSize: '20px',
            }}
            aria-label="Close"
          >
            {'\u2715'}
          </button>
        )}

        <div
          className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4"
          style={{
            background: `linear-gradient(135deg, ${requiredColor}20 0%, ${requiredColor}10 100%)`,
            border: `2px solid ${requiredColor}30`,
            boxShadow: `0 0 30px ${requiredColor}20`,
          }}
        >
          <span className="text-4xl">{'\uD83D\uDC8E'}</span>
        </div>

        <h2
          className="text-2xl font-bold mb-2"
          style={{
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            background: `linear-gradient(135deg, #F4E4A6 0%, ${requiredColor} 50%, #B8942B 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {`Откройте ${TIER_LABELS[requiredTier]}`}
        </h2>

        {featureName && (
          <p className="text-gray-400 text-base mb-4">
            {`"${featureName}" — доступно в подписке ${TIER_LABELS[requiredTier]}`}
          </p>
        )}

        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm"
          style={{
            background: 'rgba(212, 175, 55, 0.08)',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            color: '#d4af37',
          }}
        >
          <span>{'\u2B50'}</span>
          <span>{'Присоединяйтесь к 1\u2009200+ пользователям, которые уже открыли полный доступ'}</span>
        </div>
      </div>

      {/* Price block */}
      <div
        className="mx-8 px-6 py-5 rounded-2xl text-center"
        style={{
          background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(212, 175, 55, 0.05) 100%)',
          border: '1px solid rgba(212, 175, 55, 0.25)',
        }}
      >
        <div className="flex items-baseline justify-center gap-2 mb-1">
          <span className="text-4xl font-bold" style={{ color: requiredColor }}>{requiredPrice} {rub}</span>
          <span className="text-gray-400 text-sm">{'/мес'}</span>
        </div>
        <p className="text-gray-500 text-sm">
          <span>{'Это всего '}</span>
          <span className="font-semibold" style={{ color: '#d4af37' }}>{perDay} {rub} {'в день'}</span>
          <span>{mdash} {'меньше чашки кофе '}{coffee}</span>
        </p>
        {userTier !== 'free' && priceDiff > 0 && (
          <p className="text-xs text-gray-500 mt-2">
            <span>{'Доплата при переходе с '}{TIER_LABELS[userTier]}{': '}</span>
            <span className="font-semibold text-yellow-400">{'+'}{priceDiff} {rub}{'/мес'}</span>
          </p>
        )}
      </div>

      {/* Benefits */}
      <div className="px-8 py-6">
        <p className="text-gray-400 text-sm mb-3 font-medium">{'Что вы получите:'}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {benefits.map((benefit, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-3 rounded-xl"
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
              }}
            >
              <span
                className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs"
                style={{
                  background: 'rgba(34, 197, 94, 0.15)',
                  color: '#22c55e',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                }}
              >
                {'\u2713'}
              </span>
              <span className="text-gray-300 text-sm">{benefit}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tier comparison */}
      <div className="px-8 pb-6">
        <p className="text-gray-400 text-sm mb-3 font-medium">{'Сравнение тарифов:'}</p>
        <div style={{ border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '12px', overflow: 'hidden' }}>
          <div className="grid grid-cols-4 gap-0 text-center text-xs">
            {tiers.map((t) => (
              <div
                key={t.key}
                className="py-3 px-2"
                style={{
                  background: t.key === requiredTier ? `${requiredColor}15` : 'transparent',
                  borderBottom: t.key === requiredTier ? `2px solid ${requiredColor}` : '1px solid rgba(255,255,255,0.05)',
                  color: t.key === requiredTier ? requiredColor : '#888',
                  fontWeight: t.key === requiredTier ? 'bold' : 'normal',
                }}
              >
                <div className="mb-1">{TIER_LABELS[t.key as SubscriptionTier]}</div>
                <div className="text-lg font-bold">{TIER_PRICES[t.key as SubscriptionTier]?.monthly || 0} {rub}</div>
              </div>
            ))}
          </div>
          {['Прогнозы', 'Натальная карта', 'Совместимость', 'Нумерология', 'Годовой прогноз', 'Персональный анализ'].map((feature, idx) => (
            <div key={idx} className="grid grid-cols-4 gap-0 text-center text-xs" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              {tiers.map((t) => {
                const has = t.features.includes(feature);
                return (
                  <div
                    key={t.key}
                    className="py-2 px-2"
                    style={{
                      background: t.key === requiredTier ? `${requiredColor}08` : 'transparent',
                      color: has ? '#22c55e' : '#555',
                    }}
                  >
                    {has ? '\u2713' : '\u2014'}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* CTA buttons */}
      <div className="px-8 pb-8">
        <button
          onClick={handleUpgrade}
          className="w-full py-4 rounded-xl font-bold text-lg transition-all relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${requiredColor} 0%, #f4d03f 100%)`,
            color: '#0a0814',
            boxShadow: `0 8px 30px ${requiredColor}40`,
            animation: 'cta-pulse 2s ease-in-out infinite',
          }}
        >
          {`Оформить ${TIER_LABELS[requiredTier]} за ${requiredPrice} ${rub} \u2192`}
        </button>

        <div className="flex gap-3 mt-3">
          <button
            onClick={() => { navigate('/subscription'); onClose?.(); }}
            className="flex-1 py-3 rounded-xl text-sm transition-all"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#ccc',
            }}
          >
            {'Все тарифы \u2192'}
          </button>
          {onClose && (
            <button
              onClick={handleClose}
              className="flex-1 py-3 rounded-xl text-sm transition-all"
              style={{ color: '#666' }}
            >
              {'Позже'}
            </button>
          )}
        </div>
      </div>

      <div className="px-8 pb-6 text-center">
        <p className="text-xs text-gray-600">{'Отмена в любой момент \u2022 Без скрытых платежей \u2022 Мгновенный доступ'}</p>
      </div>
    </div>
  );

  if (!showInModal) return <>{ModalContent}</>;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        animation: isClosing ? 'fade-out 0.3s forwards' : 'fade-in 0.3s forwards',
      }}
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-h-[90vh] overflow-y-auto"
        style={{ display: 'flex', justifyContent: 'center' }}
      >
        <div style={{ width: '100%', maxWidth: '700px' }}>
          {ModalContent}
        </div>
      </div>
    </div>
  );
};

function getTierBenefits(tier: SubscriptionTier): string[] {
  const benefits: Record<SubscriptionTier, string[]> = {
    free: ['Базовый гороскоп', 'Число жизненного пути'],
    base: [
      '14 видов прогнозов (ежедневные, недельные, транзиты)',
      'Натальная карта с расшифровкой',
      'Совместимость с партнёром',
      'Полная нумерология (матрица, циклы, карма)',
      'Соляр, лунар, прогрессии',
      'Счастливые даты',
    ],
    premium: [
      'Всё из Base (14+ прогнозов)',
      'Годовой персональный прогноз',
      'Бизнес-прогноз для предпринимателей',
      'Кармические узлы и прошлые жизни',
      'Элективная астрология (выбор даты)',
      'Путь предназначения',
    ],
    pro: [
      'Всё из Premium (20+ прогнозов)',
      'Персональный вопрос астрологу',
      'Бизнес-совместимость',
      'Детские профили',
      'Telegram-рассылки',
      'PDF экспорт прогнозов',
    ],
  };
  return benefits[tier] || [];
}

function getTiersForComparison(requiredTier: SubscriptionTier): Array<{ key: string; features: string[] }> {
  return (['free', 'base', 'premium', 'pro'] as const).map((tier) => ({
    key: tier,
    features: getTierFeatures(tier),
  }));
}

function getTierFeatures(tier: SubscriptionTier): string[] {
  const features: Record<SubscriptionTier, string[]> = {
    free: ['Прогнозы', 'Натальная карта', 'Совместимость', 'Нумерология'],
    base: ['Прогнозы', 'Натальная карта', 'Совместимость', 'Нумерология'],
    premium: ['Прогнозы', 'Натальная карта', 'Совместимость', 'Нумерология', 'Годовой прогноз', 'Персональный анализ'],
    pro: ['Прогнозы', 'Натальная карта', 'Совместимость', 'Нумерология', 'Годовой прогноз', 'Персональный анализ'],
  };
  return features[tier] || [];
}

const styleEl = document.createElement('style');
styleEl.textContent = `
  @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
  @keyframes fade-out { from { opacity: 1; } to { opacity: 0; } }
  @keyframes pulse-glow { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
  @keyframes cta-pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.02); } }
`;
if (!document.querySelector('[data-upgrade-prompt-styles]')) {
  styleEl.setAttribute('data-upgrade-prompt-styles', 'true');
  document.head.appendChild(styleEl);
}

export default UpgradePrompt;
